import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

const DEACTIVATE_COMMIT_DELAY_MS = 40;

/** Block spurious pointer re-entries this long after close starts (live jitter ~6ms). */
const CLOSE_LOCK_MS = 50;

/** Close curtain duration (must match --submenu-close-animation-speed in base.css). */
const SUBMENU_CLOSE_ANIMATION_MS = 300;

/** Delay before layout heights drop (must match --submenu-close-layout-delay in base.css). */
const SUBMENU_CLOSE_LAYOUT_DELAY_MS = 120;

/** Close content fade duration (must match --submenu-close-fade-duration in base.css). */
const SUBMENU_CLOSE_FADE_MS = 300;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {number | undefined} */
  #deactivateRaf;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #deactivateCommitTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeLayoutTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closePanelTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeStateTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeSurfaceTimer;

  /** @type {HTMLElement | null} */
  #closingSubmenu = null;

  /** @type {HTMLElement | null} */
  #closingItem = null;

  /** @type {number} */
  #closeLockUntil = 0;

  /** Whether submenu prep (eager image loading + viewport alignment) has run. */
  #submenusPrepared = false;

  #suppressFocusActivate = false;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(() => {
      requestAnimationFrame(() => {
        this.#prepareSubmenus();
        this.#activateHoveredItem();
      });
    });
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('pointermove', this.#activateHoveredItem, { once: true });
    this.addEventListener('keydown', this.#onKeydown);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('pointermove', this.#activateHoveredItem);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    cancelAnimationFrame(this.#deactivateRaf);
    this.#deactivateRaf = undefined;
    clearTimeout(this.#deactivateCommitTimer);
    this.#deactivateCommitTimer = undefined;
    this.#abortSubmenuClose();
    this.#startCloseCurtain();
    this.#startClosePanel();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#prepareSubmenus();
  }, 100);

  #activateHoveredItem = () => {
    if (this.#state.activeItem) return;

    this.querySelector('.menu-list__list-item:hover')?.dispatchEvent(new PointerEvent('pointerenter'));
  };

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;
    if (this.#suppressFocusActivate && !(event instanceof PointerEvent)) return;

    if (event instanceof PointerEvent) {
      this.#lastPointer.x = event.clientX;
      this.#lastPointer.y = event.clientY;
    }

    const item = findMenuItem(event.target);

    // During close: absorb jitter inside the lock window, then allow genuine re-hover/switch.
    if (this.headerComponent.hasAttribute('data-submenu-closing') && event instanceof PointerEvent) {
      if (performance.now() < this.#closeLockUntil) return;
      if (!item) return;

      const { x, y } = this.#lastPointer;
      if (!this.#isPointerOverMenuItem(item, x, y)) return;
    }

    if (!item || item == this.#state.activeItem) return;

    clearTimeout(this.#deactivateCommitTimer);
    this.#deactivateCommitTimer = undefined;

    cancelAnimationFrame(this.#deactivateRaf);
    this.#deactivateRaf = undefined;
    this.#abortSubmenuClose(event instanceof FocusEvent);

    // On slow-loading pages a hover can beat the onDocumentLoaded prep, opening an
    // unaligned/mismeasured submenu. Run the prep now if it hasn't happened yet.
    if (!this.#submenusPrepared) this.#prepareSubmenus();

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      // Clear the previous submenu's active flag so the header background reverts
      // when moving to an item without a dropdown (activate() cancels the deactivate
      // RAF, so #deactivate never runs to remove it).
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
      // Commit visibility before measuring so the first open isn't a stale read.
      submenu.getBoundingClientRect();

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.#settleSubmenuHeight(submenu.offsetHeight, this.#getHeaderVisibleHeight());
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);

    // Safety net: re-measure on the next frame so the dropdown always opens with the
    // correct height even if the synchronous read above was momentarily stale.
    if (submenu && isDefaultSlot) {
      requestAnimationFrame(() => {
        if (this.#state.activeItem !== item || !this.headerComponent) return;
        const measuredHeight = submenu.offsetHeight;
        const currentHeight = this.headerComponent.style.getPropertyValue('--submenu-height');
        if (measuredHeight > 0 && `${measuredHeight}px` !== currentHeight) {
          this.#settleSubmenuHeight(measuredHeight, this.#getHeaderVisibleHeight());
        }
      });
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    const activeLink = this.#state.activeItem;
    const listItem = activeLink?.closest('.menu-list__list-item');

    if (event.relatedTarget instanceof Node) {
      if (listItem?.contains(event.relatedTarget) || menu?.contains(event.relatedTarget)) {
        return;
      }
    }

    // Opening a submenu shifts header layout, which can spuriously fire pointerleave
    // while the cursor is still over the trigger. Verify on the next frame before closing.
    if (event instanceof PointerEvent && activeLink) {
      const { clientX, clientY } = event;

      cancelAnimationFrame(this.#deactivateRaf);
      this.#deactivateRaf = requestAnimationFrame(() => {
        this.#deactivateRaf = undefined;
        if (this.#state.activeItem !== activeLink) return;

        if (this.#isPointerOverMenuItem(activeLink, clientX, clientY)) {
          return;
        }

        clearTimeout(this.#deactivateCommitTimer);
        this.#deactivateCommitTimer = setTimeout(() => {
          this.#deactivateCommitTimer = undefined;
          if (this.#state.activeItem !== activeLink) return;

          const { x, y } = this.#lastPointer;
          if (this.#isPointerOverMenuItem(activeLink, x, y)) {
            return;
          }

          this.#deactivate();
        }, DEACTIVATE_COMMIT_DELAY_MS);
      });
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate: fade content, then sweep curtain after a short overlap delay.
   * Layout heights stay open during the fade so the curtain stays behind the dropdown.
   * @param {HTMLElement | null} [item]
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const item = this.#state.activeItem;
    if (!item) return;

    const trigger = item.closest('li')?.querySelector('[ref="menuitem"]') ?? item;

    this.#deactivate(item);

    if (trigger instanceof HTMLElement) {
      this.#suppressFocusActivate = true;
      trigger.focus();
      this.#suppressFocusActivate = false;
    }
  };

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    if (this.headerComponent?.hasAttribute('data-submenu-closing')) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.#clearCloseTimers();

    const submenu = findSubmenu(item);

    if (submenu) {
      this.#closingSubmenu = submenu;
      this.#closingItem = item;
      this.#closeLockUntil = performance.now() + CLOSE_LOCK_MS;
      // Lock the glass pill transparent before aria-expanded clears so the blur
      // does not snap back during the content fade-out.
      this.headerComponent?.setAttribute('data-submenu-surface-closing', '');
      this.headerComponent?.setAttribute('data-submenu-closing', '');
    }

    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      const layoutDelay = this.#getSubmenuCloseLayoutDelayMs();
      const fadeDuration = this.#getSubmenuCloseFadeMs();
      const clipDuration = this.#getSubmenuCloseAnimationMs();

      // Curtain height only — clip/panel stay open so content isn't clipped over the white sheet.
      this.#closeLayoutTimer = setTimeout(() => {
        this.#closeLayoutTimer = undefined;
        this.#startCloseCurtain();
      }, layoutDelay);

      // Clip + layout after the content fade finishes.
      this.#closePanelTimer = setTimeout(() => {
        this.#closePanelTimer = undefined;
        this.#startClosePanel();
      }, fadeDuration);

      // Restore pill background + blur once the curtain reaches the header bottom.
      this.#closeSurfaceTimer = setTimeout(() => {
        this.#closeSurfaceTimer = undefined;
        this.#finishCloseSurface();
      }, layoutDelay + clipDuration);

      this.#closeStateTimer = setTimeout(() => {
        this.#closeStateTimer = undefined;
        this.#finishCloseLayout();
      }, fadeDuration + clipDuration);
    } else {
      this.#startCloseCurtain();
      this.#startClosePanel();
      this.#finishCloseLayout();
    }
  };

  /**
   * Shrink the white curtain only (underlay uses --full-open-header-height).
   */
  #startCloseCurtain() {
    this.headerComponent?.style.setProperty('--full-open-header-height', '0px');
  }

  /**
   * Collapse the dropdown clip/panel (uses --submenu-panel-height).
   */
  #startClosePanel() {
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.headerComponent?.style.setProperty('--submenu-panel-height', '0px');
  }

  /**
   * Clear scheduled close timers without touching closing layout state.
   */
  #clearCloseTimers() {
    clearTimeout(this.#closeLayoutTimer);
    this.#closeLayoutTimer = undefined;
    clearTimeout(this.#closePanelTimer);
    this.#closePanelTimer = undefined;
    clearTimeout(this.#closeStateTimer);
    this.#closeStateTimer = undefined;
    clearTimeout(this.#closeSurfaceTimer);
    this.#closeSurfaceTimer = undefined;
  }

  /**
   * Abort an in-progress or pending submenu close (menu switch, disconnect).
   */
  #abortSubmenuClose(ignoreCloseLock = false) {
    if (
      !ignoreCloseLock &&
      this.headerComponent?.hasAttribute('data-submenu-closing') &&
      performance.now() < this.#closeLockUntil
    ) {
      return;
    }

    this.#clearCloseTimers();

    this.headerComponent?.removeAttribute('data-submenu-closing');
    this.headerComponent?.removeAttribute('data-submenu-surface-closing');

    if (this.#closingSubmenu) {
      delete this.#closingSubmenu.dataset.active;
      this.#closingSubmenu = null;
    }

    this.#closingItem = null;
    this.#closeLockUntil = 0;
  }

  /**
   * Restore header pill background + blur after the close curtain animation completes.
   */
  #finishCloseSurface() {
    this.headerComponent?.removeAttribute('data-submenu-surface-closing');

    if (this.#closingSubmenu) {
      delete this.#closingSubmenu.dataset.active;
    }
  }

  /**
   * Clear submenu closing layout state after panel collapse finishes.
   */
  #finishCloseLayout() {
    this.headerComponent?.removeAttribute('data-submenu-closing');

    if (this.#closingSubmenu) {
      delete this.#closingSubmenu.dataset.active;
      this.#closingSubmenu = null;
    }

    this.#closingItem = null;
    this.#closeLockUntil = 0;
  }

  /**
   * Whether the pointer at (x, y) is over the menu link, list item, or submenu.
   * @param {HTMLElement} activeLink
   * @param {number} x
   * @param {number} y
   * @returns {boolean}
   */
  #isPointerOverMenuItem(activeLink, x, y) {
    const hit = document.elementFromPoint(x, y);
    if (!(hit instanceof Element)) return false;

    const submenu = findSubmenu(activeLink);
    const listItem = activeLink.closest('.menu-list__list-item');

    return activeLink.contains(hit) || Boolean(listItem?.contains(hit)) || Boolean(submenu?.contains(hit));
  }

  /**
   * Delay before layout heights drop on close (matches CSS).
   * @returns {number}
   */
  #getSubmenuCloseLayoutDelayMs() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;

    return SUBMENU_CLOSE_LAYOUT_DELAY_MS;
  }

  /**
   * Duration of the submenu content opacity fade on close (matches CSS).
   * @returns {number}
   */
  #getSubmenuCloseFadeMs() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;

    return SUBMENU_CLOSE_FADE_MS;
  }

  /**
   * Duration of the close curtain / clip-path sweep (matches CSS).
   * @returns {number}
   */
  #getSubmenuCloseAnimationMs() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;

    return SUBMENU_CLOSE_ANIMATION_MS;
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top')?.offsetHeight ?? 0)
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent.style.setProperty('--submenu-panel-height', `${fullOpenHeaderHeight}px`);
    this.headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Write a corrected submenu height after the open transition has already started,
   * without re-animating the clip-path (a second, visible resize reads as a flicker).
   * The `data-submenu-settling` flag drops the submenu transition for one frame so the
   * panel snaps to the right height, then the flag is removed and transitions resume.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #settleSubmenuHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    this.headerComponent.dataset.submenuSettling = '';
    this.headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.#setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight);
    // Force a style/layout flush so the snap commits while transitions are off.
    void this.headerComponent.offsetHeight;

    requestAnimationFrame(() => {
      if (this.headerComponent) delete this.headerComponent.dataset.submenuSettling;
    });
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    if (!window.matchMedia('(min-width: 990px) and (hover: hover)').matches) return;

    const eagerLoad = () => {
      const images = this.querySelectorAll('img[loading="lazy"]');
      images?.forEach((image) => image.removeAttribute('loading'));
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(eagerLoad, { timeout: 3000 });
    } else {
      setTimeout(eagerLoad, 1);
    }
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  /**
   * Eager-load submenu images and pre-calculate viewport offsets. Runs on document
   * load, on resize, and lazily from activate() so no submenu opens unprepared.
   */
  #prepareSubmenus() {
    this.#submenusPrepared = true;
    this.#preloadImages();
    this.#alignAllSubmenusToViewport();
  }

  /**
   * Pre-calculate viewport offsets for all desktop megamenus.
   */
  #alignAllSubmenusToViewport() {
    const submenus = this.querySelectorAll('.menu-list__list-item:not([slot="overflow"]) > .menu-list__submenu');

    submenus.forEach((submenu) => {
      if (submenu instanceof HTMLElement) {
        this.#alignSubmenuToViewport(submenu);
      }
    });
  }

  /**
   * Shift a desktop megamenu panel so its left edge aligns with the viewport.
   * header-menu uses transform for nav placement, which traps absolute descendants.
   * @param {HTMLElement} submenu
   */
  #alignSubmenuToViewport(submenu) {
    if (!submenu.classList.contains('menu-list__submenu')) {
      this.#clearSubmenuViewportOffset(submenu);
      return;
    }

    if (!window.matchMedia('(min-width: 1025px)').matches) {
      this.#clearSubmenuViewportOffset(submenu);
      return;
    }

    const listItem = submenu.closest('.menu-list__list-item');
    if (listItem?.slot === 'overflow') {
      this.#clearSubmenuViewportOffset(submenu);
      return;
    }

    submenu.style.removeProperty('--sc-submenu-viewport-offset');
    void submenu.offsetWidth;
    const { left } = submenu.getBoundingClientRect();
    submenu.style.setProperty('--sc-submenu-viewport-offset', `${-left}px`);
  }

  /**
   * @param {HTMLElement | null | undefined} submenu
   */
  #clearSubmenuViewportOffset(submenu) {
    submenu?.style.removeProperty('--sc-submenu-viewport-offset');
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
